import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
    ICommunicationDetails,
    ICompanyDetails,
    IInvoicingDetails,
} from '../interfaces/ISettingsDetails';
import { ISetting } from '../../../api/settings/interface/ISetting';

interface ISettingsDetailInitialState {
    companyDetails: ICompanyDetails;
    communicationDetails: ICommunicationDetails;
    settingsList: ISetting[];
    invoicingDetails: IInvoicingDetails;
    canEditSettings: boolean;
    quickBooksUserName: string;
    isQBUserAvailable: boolean;
    showConnectToQuickBooksModal: boolean;
    showQBUserDetailsModal: boolean;
    showQBToggleAlertModal: boolean;
    showSaveChangesReminderModal: boolean;
    showQBAuthErrorModal: boolean;
    isAddQBUser: boolean;
    passwordExpirationMonths: number;
    IsBolRequiredInMobileApp: boolean;
    showHeadCountOrQuantity: string;
}

const initialState: ISettingsDetailInitialState = {
    companyDetails: {
        companyName: '',
        companyAddress: '',
        phoneNumber: '',
        dispatchSupportNumber: '',
        emailAddress: '',
        companyLogo: '',
        brokerFeeType: '',
        brokerFeeAmount: '',
        faxNumber: '',
        city: '',
        state: '',
        zipCode: '',
        baseSettingId: 0,
        settlementReportBatchNumber: '',
    },
    communicationDetails: {
        isGreetingMessageIncluded: false,
        greetingMessage: '',
        isOrderNumberIncluded: false,
        isDateIncluded: false,
        isTimeFormat12Hr: false,
        isPinDropNameIncluded: false,
        isLocationInstructionIncluded: false,
        isEstimatedWeightIncluded: false,
        isEstimatedHeadCountIncluded: false,
        isCommodityIncluded: false,
        isDriverInstructionIncluded: false,
        isEndingMessageIncluded: false,
        endingMessage: '',
    },
    settingsList: [],
    invoicingDetails: {
        isSendInvoices: false,
        invoiceRecipients: [],
        isQuickBooksConnected: false,
        isRoundInvoiceTotals: false,
        isQuickBooksOnlineConnected: false,
    },
    canEditSettings: false,
    quickBooksUserName: '',
    isQBUserAvailable: false,
    showConnectToQuickBooksModal: false,
    showQBUserDetailsModal: false,
    showQBToggleAlertModal: false,
    showSaveChangesReminderModal: false,
    showQBAuthErrorModal: false,
    isAddQBUser: true,
    passwordExpirationMonths: 0,
    IsBolRequiredInMobileApp: true,
    showHeadCountOrQuantity: '',
};

export const settingsDetail = createSlice({
    name: 'settingsDetail',
    initialState: initialState,
    reducers: {
        setCompanyDetails: (state, action: PayloadAction<ICompanyDetails>) => {
            state.companyDetails = action.payload;
        },
        setCommunicationDetails: (
            state,
            action: PayloadAction<ICommunicationDetails>,
        ) => {
            state.communicationDetails = action.payload;
        },
        setSettingsList: (state, action: PayloadAction<ISetting[]>) => {
            state.settingsList = action.payload;
        },
        setInvoicingDetails: (
            state,
            action: PayloadAction<IInvoicingDetails>,
        ) => {
            state.invoicingDetails = action.payload;
        },
        setCanEditSettings: (state, action: PayloadAction<boolean>) => {
            state.canEditSettings = action.payload;
        },
        setQuickBooksUserName: (state, action: PayloadAction<string>) => {
            state.quickBooksUserName = action.payload;
        },
        setIsQBUserAvailable: (state, action: PayloadAction<boolean>) => {
            state.isQBUserAvailable = action.payload;
        },
        setShowConnectToQuickBooksModal: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.showConnectToQuickBooksModal = action.payload;
        },
        setShowQBUserDetailsModal: (state, action: PayloadAction<boolean>) => {
            state.showQBUserDetailsModal = action.payload;
        },
        setShowQBToggleAlertModal: (state, action: PayloadAction<boolean>) => {
            state.showQBToggleAlertModal = action.payload;
        },
        setShowSaveChangesReminderModal: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.showSaveChangesReminderModal = action.payload;
        },
        setShowQBAuthErrorModal: (state, action: PayloadAction<boolean>) => {
            state.showQBAuthErrorModal = action.payload;
        },
        setIsAddQBUser: (state, action: PayloadAction<boolean>) => {
            state.isAddQBUser = action.payload;
        },
        setPasswordExpirationMonths: (state, action: PayloadAction<number>) => {
            state.passwordExpirationMonths = action.payload;
        },
        setIsBolRequiredInMobileApp: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.IsBolRequiredInMobileApp = action.payload;
        },
        setShowHeadCountOrQuantity: (state, action: PayloadAction<string>) => {
            state.showHeadCountOrQuantity = action.payload;
        },
    },
});

export const {
    setCompanyDetails,
    setSettingsList,
    setCommunicationDetails,
    setInvoicingDetails,
    setCanEditSettings,
    setQuickBooksUserName,
    setIsQBUserAvailable,
    setPasswordExpirationMonths,
    setShowConnectToQuickBooksModal,
    setShowQBUserDetailsModal,
    setIsAddQBUser,
    setIsBolRequiredInMobileApp,
    setShowHeadCountOrQuantity,
    setShowQBToggleAlertModal,
    setShowSaveChangesReminderModal,
    setShowQBAuthErrorModal,
} = settingsDetail.actions;

const settingsReducer = settingsDetail.reducer;
export default settingsReducer;
